import { defineStore } from "pinia";
import { ref, Ref } from "vue";
import {
  INewTask,
  ITask,
  ITaskAssign,
  ITaskComment,
  ITaskFilter,
  ITaskEvent,
  ITaskData,
  IMyTaskFilter,
} from "@/features/tasks/task.types";
import { ITableHeader } from "@/types/table";
import { taskService } from "@/features/tasks/task.service";
import { useNotification } from "@/utils/notification";

const { pushNotification } = useNotification();

export const useTaskStore = defineStore("task", () => {
  const taskHeaders: ITableHeader[] = [
    {
      name: "id",
      label: "No",
      width: "3%",
    },
    {
      name: "company",
      label: "Company",
      width: "14%",
    },
    {
      name: "drivers",
      label: "Drivers",
      width: "14%",
    },
    {
      name: "team",
      label: "Team",
      width: "5%",
    },
    {
      name: "users",
      label: "User",
      width: "13%",
    },
    {
      name: "date",
      label: "Start time",
      width: "6%",
    },
    {
      name: "completed_time",
      label: "End time",
      width: "6%",
    },
    {
      name: "points_awarded",
      label: "KPI",
      width: "3%",
    },
    {
      name: "status",
      label: "Status",
      width: "9%",
    },
    {
      name: "problems",
      label: "Problems",
      width: "24%",
    },
    {
      name: "actions",
      label: "",
      width: "3%",
    },
  ];

  const taskHeadersUser: ITableHeader[] = [
    {
      name: "id",
      label: "No",
      width: "3%",
    },
    {
      name: "company",
      label: "Company",
      width: "22%",
    },
    {
      name: "drivers",
      label: "Drivers",
      width: "22%",
    },
    {
      name: "date",
      label: "Date",
      width: "6%",
    },
    {
      name: "points_awarded",
      label: "KPI",
      width: "3%",
    },
    {
      name: "status",
      label: "Status",
      width: "10%",
    },
    {
      name: "problems",
      label: "Problems",
      width: "31%",
    },
    {
      name: "actions",
      label: "",
      width: "3%",
    },
  ];

  const commonFilter = ref<ITaskFilter>({
    page: 1,
    per_page: 50,
    status: "",
    assignee_id: "",
    shift_run_id: "",
    team_id: "",
    work_type_id: "",
    company_q: "",
    search: "",
    driver_q: "",
    created_from: "",
    created_to: "",
    order_by: "",
    order_dir: "desc",
  });

  const myTaskFilter = ref<IMyTaskFilter>({
    search: "",
    page: 1,
    per_page: 50,
    status: "",
    work_type_id: "",
  });

  async function createTask(loading: Ref<boolean>, payload: INewTask, cl: any) {
    loading.value = true;
    await taskService
      .createTask(payload)
      .then((res) => {
        cl(res);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function createInformTask(
    loading: Ref<boolean>,
    payload: INewTask,
    cl: any
  ) {
    loading.value = true;
    await taskService
      .createInformNote(payload)
      .then((res) => {
        cl(res);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function getTasks(loading: Ref<boolean>, cl: any) {
    loading.value = true;
    await taskService
      .getTasks({ ...commonFilter.value })
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function getAlertQueueCount(loading: Ref<boolean>, team_id?: string) {
    try {
      loading.value = true;
      const { data } = await taskService.getAlertQueueCount(team_id);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return 0;
    } finally {
      loading.value = false;
    }
  }

  async function takeAlert(loading: Ref<boolean>) {
    try {
      loading.value = true;
      const { data } = await taskService.takeAlert();
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function getMyQueue(loading: Ref<boolean>, cl: any) {
    loading.value = true;
    await taskService
      .getMyQueue({ ...myTaskFilter.value })
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function getTaskById(
    loading: Ref<boolean>,
    id: string
  ): Promise<ITask> {
    try {
      loading.value = true;
      const { data } = await taskService.getTaskById(id);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function getTaskEvents(
    loading: Ref<boolean>,
    taskId: string
  ): Promise<ITaskEvent[]> {
    try {
      loading.value = true;
      const { data } = await taskService.getTaskEvents(taskId);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [] as any;
    } finally {
      loading.value = false;
    }
  }

  async function startTask(loading: Ref<boolean>, id: string) {
    try {
      loading.value = true;
      const { data } = await taskService.startTask(id);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function holdTask(loading: Ref<boolean>, id: string) {
    try {
      loading.value = true;
      const { data } = await taskService.holdTask(id);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function resumeTask(loading: Ref<boolean>, id: string) {
    try {
      loading.value = true;
      const { data } = await taskService.resumeTask(id);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function doneTask(loading: Ref<boolean>, id: string) {
    try {
      loading.value = true;
      const { data } = await taskService.doneTask(id);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function closeTask(loading: Ref<boolean>, id: string, cl: any) {
    loading.value = true;
    await taskService
      .closeTask(id)
      .then((res: any) => {
        cl(res);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function failTask(loading: Ref<boolean>, id: string) {
    try {
      loading.value = true;
      const { data } = await taskService.failTask(id);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function assignTask(
    loading: Ref<boolean>,
    taskId: string,
    payload: ITaskAssign
  ) {
    try {
      loading.value = true;
      const { data } = await taskService.assignTask(taskId, payload);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function autoAssign(
    loading: Ref<boolean>,
    shift_run_id: string,
    limit: number,
    team_id: string
  ) {
    try {
      loading.value = true;
      const { data } = await taskService.autoAssign(
        shift_run_id,
        limit,
        team_id
      );
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function addComment(
    loading: Ref<boolean>,
    taskId: string,
    payload: ITaskComment,
    cl: any
  ) {
    loading.value = true;
    await taskService
      .addComment(taskId, payload)
      .then((res: any) => {
        cl(res);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function getRecommendedWorker(loading: Ref<boolean>, taskId: string) {
    try {
      loading.value = true;
      const { data } = await taskService.getRecommendedWorker(taskId);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function changeTaskWorkType(
    loading: Ref<boolean>,
    taskId: string,
    payload: any,
    cl: any
  ) {
    loading.value = true;
    await taskService
      .changeTaskWorkType(taskId, payload)
      .then((res: any) => {
        cl(res);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function addTaskWorkType(
    loading: Ref<boolean>,
    taskId: string,
    workTypeId: string
  ): Promise<boolean> {
    if (loading.value) return false;

    loading.value = true;

    try {
      await taskService.addTaskWorkType(taskId, workTypeId);

      pushNotification("Additional work type added successfully", "success");

      return true;
    } catch (err: any) {
      pushNotification(
        err.response?.data?.detail || "Failed to add additional work type",
        "error"
      );

      return false;
    } finally {
      loading.value = false;
    }
  }

  async function removeTaskWorkType(
    loading: Ref<boolean>,
    taskId: string,
    workTypeId: string
  ): Promise<boolean> {
    if (loading.value) return false;

    loading.value = true;

    try {
      await taskService.removeTaskWorkType(taskId, workTypeId);

      pushNotification("Additional work type removed successfully", "success");

      return true;
    } catch (err: any) {
      pushNotification(
        err.response?.data?.detail || "Failed to remove additional work type",
        "error"
      );

      return false;
    } finally {
      loading.value = false;
    }
  }

  async function getTaskTimeline(
    loading: Ref<boolean>,
    taskId: string,
    cl: any
  ) {
    loading.value = true;
    await taskService
      .getTaskTimeline(taskId)
      .then((res: any) => {
        cl(res);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function getReassignSuggestions(
    loading: Ref<boolean>,
    taskId: string,
    cl: any
  ) {
    loading.value = true;
    await taskService
      .getReassignSuggestions(taskId)
      .then((res: any) => {
        cl(res);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function reassignTask(
    loading: Ref<boolean>,
    taskId: string,
    payload: any,
    cl: any
  ) {
    loading.value = true;
    await taskService
      .reassignTask(taskId, payload)
      .then((res: any) => {
        cl(res);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  const clearCommonFilter = () => {
    commonFilter.value = {
      page: 1,
      per_page: 50,
      status: "",
      assignee_id: "",
      shift_run_id: "",
      team_id: "",
      work_type_id: "",
      company_q: "",
      search: "",
      driver_q: "",
      created_from: "",
      created_to: "",
      order_by: "",
      order_dir: "desc",
    };
  };

  const clearMyTaskFilter = () => {
    myTaskFilter.value = {
      search: "",
      page: 1,
      per_page: 50,
      status: "",
      work_type_id: "",
    };
  };

  return {
    taskHeaders,
    taskHeadersUser,
    commonFilter,
    myTaskFilter,
    createTask,
    createInformTask,
    getTasks,
    getAlertQueueCount,
    takeAlert,
    getMyQueue,
    getTaskById,
    getTaskEvents,
    startTask,
    holdTask,
    resumeTask,
    doneTask,
    closeTask,
    failTask,
    assignTask,
    autoAssign,
    addComment,
    getRecommendedWorker,
    changeTaskWorkType,
    addTaskWorkType,
    removeTaskWorkType,
    clearCommonFilter,
    clearMyTaskFilter,
    getTaskTimeline,
    getReassignSuggestions,
    reassignTask,
  };
});
